import { unaryRpc } from "@redotech/rpc/definition";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

const quoteAddressSchema = z.object({
  country: z.string().min(1),
  postalCode: z.string().min(1),
  state: z.string().optional(),
  city: z.string().optional(),
  line1: z.string().optional(),
});

export type QuoteAddress = z.infer<typeof quoteAddressSchema>;

const quoteDestinationSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("returnsCenter"), returnsCenterId: z.string() }),
  /** One of the team's return locations, a hand-entered address, or else the team's default location. */
  z.object({
    kind: z.literal("merchantLocation"),
    returnLocationId: zExt.objectId().optional(),
    address: quoteAddressSchema.optional(),
  }),
]);

export type QuoteDestination = z.infer<typeof quoteDestinationSchema>;

const calculatedMarketRateSchema = z.object({
  marketRate: z.string(),
  carrier: z.string(),
  serviceLevel: z.string(),
  listedRate: z.string(),
  defaultDiscountPercent: z.number(),
  teamDiscountPercent: z.number(),
});

/** The UPS eu rate-card cost basis, in UPS's own currency. */
const upsRateCardPricingSchema = z.object({
  source: z.string(),
  currency: z.string(),
  cardRate: z.string().optional(),
  cardBase: z.string().optional(),
  fuelRate: z.number().optional(),
  fuel: z.string().optional(),
  accessorials: z.string().optional(),
  cardZone: z.string().optional(),
  carrierZone: z.string().optional(),
  billableWeightKg: z.number().optional(),
});

const includedChargeKinds = ["fuel", "accessorials", "duties"] as const;

const includedChargeSources = [
  "ups_rate_card",
  "carrier_estimate",
  "carrier_charge",
] as const;

/** A charge the carrier itemized; it is already part of `carrierCost`. */
const includedChargeSchema = z.object({
  kind: z.enum(includedChargeKinds),
  amount: z.string(),
  currency: z.string(),
  source: z.enum(includedChargeSources),
});

export type IncludedCharge = z.infer<typeof includedChargeSchema>;

/**
 * `not-billed` carriers never pass adjustments on; `contract-all-in` rates bill exactly the
 * quote. Everything else carries the default buffer until invoice history lands.
 */
export const SurchargeEstimateSource = {
  DEFAULT_BUFFER: "default-buffer",
  NOT_BILLED: "not-billed",
  CONTRACT_ALL_IN: "contract-all-in",
} as const;
export type SurchargeEstimateSource =
  (typeof SurchargeEstimateSource)[keyof typeof SurchargeEstimateSource];

/** Post-purchase surcharges, as a percent of and an amount on top of `carrierCost`. */
const surchargeBucketEstimateSchema = z.object({
  upliftPct: z.number(),
  amount: z.string(),
  currency: z.string(),
  source: z.enum(SurchargeEstimateSource),
  /** Only when the merchant passes label adjustments through. */
  includedInDefaultTotal: z.boolean(),
});

export type SurchargeBucketEstimate = z.infer<
  typeof surchargeBucketEstimateSchema
>;

/** What the carrier is expected to bill after purchase; null for merchant-account rates. */
const surchargeEstimateSchema = z.object({
  otherAccessorials: surchargeBucketEstimateSchema,
  /** Null when the carrier doesn't say whether its quote includes fuel. */
  fuelIncludedInLabelCost: z.boolean().nullable(),
});

export type SurchargeEstimate = z.infer<typeof surchargeEstimateSchema>;

const quoteRateSchema = z.object({
  rateId: z.string(),
  /** The rate a real return on this lane would be billed at. */
  selected: z.boolean(),
  carrier: z.string(),
  service: z.string(),
  providerType: z.string(),
  shippingAccount: z.string().optional(),
  carrierAccountId: z.string().optional(),
  isMerchantCarrierAccount: z.boolean(),
  sendcloudContractId: z.number().optional(),
  /** What the carrier charges, converted to `currency`. */
  carrierCost: z.string(),
  marketRate: z.string().optional(),
  calculatedMarketRate: calculatedMarketRateSchema.optional(),
  merchantOverrideApplied: z.boolean(),
  insuranceCost: z.number(),
  /** The label price the merchant is billed. */
  merchantPrice: z.string(),
  upsRateCardPricing: upsRateCardPricingSchema.optional(),
  includedCharges: z.array(includedChargeSchema),
  surchargeEstimate: surchargeEstimateSchema.nullable(),
});

export type QuoteRate = z.infer<typeof quoteRateSchema>;

/** A merchant setting that changed this quote's carriers or price. */
const quoteSettingAppliedSchema = z.object({
  label: z.string(),
  /** The setting as configured, e.g. "UPS, USPS" or "on". */
  value: z.string(),
  /** What it did to this quote. */
  effect: z.string(),
  /** The setting is configured but did not shape this quote; a real return may differ. */
  notApplied: z.boolean(),
  /** Returned rates whose price or presence it set; empty when it only removed rates. */
  rateIds: z.array(z.string()),
});

export type QuoteSettingApplied = z.infer<typeof quoteSettingAppliedSchema>;

const resolvedAddressSchema = z.object({
  street1: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  country: z.string().nullish(),
});

export const quoteReturnLabel = unaryRpc({
  name: "quoteReturnLabel" as const,
  input: z.object({
    /** Omitted: the baseline team prices Redo carriers at default settings. */
    teamId: zExt.objectId().optional(),
    origin: quoteAddressSchema,
    destination: quoteDestinationSchema,
    weightOunces: z.number().positive(),
    dimensionsInches: z
      .object({
        length: z.number().positive(),
        width: z.number().positive(),
        height: z.number().positive(),
      })
      .optional(),
    /** Customs and insurance value in the team's shop currency; defaults to 50. */
    declaredValue: z.number().positive().optional(),
  }),
  output: z.object({
    quotedAt: z.string(),
    currency: z.string(),
    team: z.object({
      id: z.string(),
      name: z.string(),
      isBaseline: z.boolean(),
      passThroughLabelAdjustments: z.boolean(),
    }),
    origin: resolvedAddressSchema,
    destination: resolvedAddressSchema.extend({
      returnsCenterId: z.string().optional(),
    }),
    weightOunces: z.number(),
    declaredValue: z.number(),
    uspsZone: z.number().optional(),
    rates: z.array(quoteRateSchema),
    /** The rate a real return on this lane would be billed at. */
    selectedRateId: z.string().optional(),
    cheapestRateId: z.string().optional(),
    noRatesReason: z.string().optional(),
    /** Merchant quotes only; absent for baseline quotes. */
    settingsApplied: z.array(quoteSettingAppliedSchema).optional(),
  }),
});

export type QuoteReturnLabelOutput = z.infer<typeof quoteReturnLabel.output>;
